import { useCallback, useState } from 'react';
import {
  TKycUserInfo,
  useCreateUserKycRecord as useCreateUserKycMutation,
} from '@xhype/card-app-sdk';
import * as Sentry from '@sentry/react';
import { useLocation } from 'react-router-dom';

/* Types */
type TUseHandleInitiateUserKycProps = {
  id: number | undefined;
  kycUserInfo: TKycUserInfo | undefined;
  isLoadingKycUserInfo: boolean;
};

/* Hook */
export const useHandleInitiateUserKyc = ({
  id,
  kycUserInfo,
  isLoadingKycUserInfo,
}: TUseHandleInitiateUserKycProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const location = useLocation();

  /* Mutations */
  const createUserKycRecord = useCreateUserKycMutation({
    onError: (error: any) => {
      setError(error?.response?.data?.msg);
      console.log(
        `Create user kyc record: Unexpected error - ${error.message}`,
      );
    },
    onSuccess: () => {
      console.log('Create user kyc record: Successfully created');
    },
  });

  /* Callbacks */
  const redirectToVerinex = useCallback(() => {
    const origin = window.location.origin;
    const pathname = location.pathname;
    const originUrl = `${origin}${pathname}`;

    const verinexUrl = `https://verinex.com/kyc?origin_url=${encodeURIComponent(originUrl)}&user_id=${id}`;
    window.location.href = verinexUrl;
  }, [id, location]);

  const handleInitiateUserKyc = useCallback(async () => {
    if (!id || isLoadingKycUserInfo) return;

    try {
      setIsLoading(true);

      if (!kycUserInfo?.id) {
        await createUserKycRecord.mutateAsync({
          userId: id,
          token: '',
          status: 'PENDING',
        });

        redirectToVerinex();
      } else if (kycUserInfo?.status === 'PENDING') {
        redirectToVerinex();
      }
    } catch (error) {
      Sentry.captureException({
        message: `Failed to initiate user ${id} kyc`,
        extra: {
          error,
        },
      });
      throw new Error(`Failed to initiate user kyc: ${error}`);
    } finally {
      setIsLoading(false);
    }
  }, [
    id,
    createUserKycRecord,
    isLoadingKycUserInfo,
    kycUserInfo,
    redirectToVerinex,
  ]);

  /* Return */
  return {
    handleInitiateUserKyc,
    isLoading,
    error,
  };
};
